import { Box, Card, CardContent, Typography } from '@mui/material'
import { useDispatch } from 'react-redux'
import { cli } from '../../config/init'
import TwaySimpleControlForm from '../../components/TwaySimpleControlForm'
import { loadHouses } from '../../store/HouseContainer/operations'
import { Control, EventFull, House, Scenario } from '../../types'

type Props = {
	house: House
	scenario: Scenario
	control: Control
	eventss: EventFull[]
}

type TwayTemps = {
	heatingTempMin: number
	heatingTempMax: number
	coolingTempMin: number
	coolingTempMax: number
}

// フォールバック既定値は backend (app/models/control.rb) に一本化する。
const DEFAULTS: TwayTemps = {
	heatingTempMin: 10,
	heatingTempMax: 20,
	coolingTempMin: 20,
	coolingTempMax: 30,
}

// tway event が温度フィールドを持つか判定（backend は返すが型が union のため絞り込む）
function isTwayEvent(event: EventFull): event is EventFull & TwayTemps {
	const e = event as Partial<TwayTemps>
	return (
		typeof e.heatingTempMin === 'number' && typeof e.coolingTempMin === 'number'
	)
}

const TwayManageTab = ({ scenario, control, eventss }: Props) => {
	const dispatch = useDispatch()

	// #1: state で保持せず毎レンダー props から導出する。
	// MachinePage は sid が変わっても再マウントされないため、state 保持だと
	// 別シナリオの event を掴んだまま保存してしまう（＝別シナリオを上書きする）。
	const subControlIds = control.subControlIds || []
	const twayEvent = eventss.find((e) => subControlIds.includes(e.subControlId))

	const handleSave = async (
		pattern: 'heating' | 'cooling',
		config: { tempFull: number; tempClose: number },
	) => {
		if (!twayEvent?.id) {
			alert('イベントIDが見つかりません')
			return
		}

		// 反対モードは event の現在値を維持し、無い場合のみ backend 既定を使う。
		const cur = isTwayEvent(twayEvent) ? twayEvent : DEFAULTS
		const params = {
			event: {
				heating_temp_min:
					pattern === 'heating' ? config.tempFull : cur.heatingTempMin,
				heating_temp_max:
					pattern === 'heating' ? config.tempClose : cur.heatingTempMax,
				cooling_temp_min:
					pattern === 'cooling' ? config.tempClose : cur.coolingTempMin,
				cooling_temp_max:
					pattern === 'cooling' ? config.tempFull : cur.coolingTempMax,
			},
		}

		try {
			await cli().put(`/events/${twayEvent.id}`, params)
			// redux を更新すれば eventss 経由で twayEvent が再導出される。
			await dispatch(loadHouses())
			alert(
				`${pattern === 'heating' ? '加温' : '冷却'}モードの設定を保存しました`,
			)
		} catch {
			alert('保存に失敗しました')
		}
	}

	if (!twayEvent) {
		return (
			<Box sx={{ py: 3 }}>
				<Card>
					<CardContent>
						<Typography variant="h6" color="error" gutterBottom>
							⚠️ 設定データが見つかりません
						</Typography>
						<Typography variant="body1" color="text.secondary" gutterBottom>
							この三方弁機器に紐づく設定イベントが見つかりませんでした。
						</Typography>
						<Typography variant="body2" color="text.secondary">
							シナリオ: {scenario.name}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Control ID: {control.id}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							SubControl IDs: {JSON.stringify(subControlIds)}
						</Typography>
					</CardContent>
				</Card>
			</Box>
		)
	}

	return (
		<Box sx={{ py: 3 }}>
			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Typography variant="body1" color="text.secondary" gutterBottom>
						三方弁の簡易制御設定です。温度に応じて加温または冷却を行います。
					</Typography>
					<Typography variant="body2" color="text.secondary">
						設定はこのシナリオ（{scenario.name}）内でのみ有効です。
					</Typography>
				</CardContent>
			</Card>

			<TwaySimpleControlForm
				// #1: event が変わったら再マウントして初期値を必ず現在の DB 値に同期する。
				key={twayEvent.id}
				onSave={handleSave}
				initialHeating={
					isTwayEvent(twayEvent)
						? {
								tempFull: twayEvent.heatingTempMin,
								tempClose: twayEvent.heatingTempMax,
						  }
						: undefined
				}
				initialCooling={
					isTwayEvent(twayEvent)
						? {
								tempFull: twayEvent.coolingTempMax,
								tempClose: twayEvent.coolingTempMin,
						  }
						: undefined
				}
			/>
		</Box>
	)
}

export default TwayManageTab
